import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  ElementRef,
  computed,
  effect,
  inject,
  input,
  model,
  output,
  untracked,
  ViewChild,
  signal,
  HostListener
} from '@angular/core';
import { CdkVirtualScrollViewport } from '@angular/cdk/scrolling';
import { Overlay, OverlayModule } from '@angular/cdk/overlay';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CommonModule } from '@angular/common';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatMenuModule } from '@angular/material/menu';
import { ScrollingModule } from '@angular/cdk/scrolling';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { FormsModule } from '@angular/forms';
import { MatInputModule } from '@angular/material/input';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatRadioModule } from '@angular/material/radio';
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader';
import { MatCheckboxModule } from '@angular/material/checkbox';
import { KeyboardShortcutService } from '@/cpm/shared/services/keyboard-shortcut.service';
import { KEYBOARD_SHORTCUT_COMMANDS, OPEN_SITE_SELECTION_SHORTCUT_KEYS } from '@/cpm/shared/constants/keyboard-shortcut.constants';
import { NavigableOption, SiteSelectionDropdownConfig } from '@/cpm/models/site-entity.model';
import { ShowtooltipDirective } from '@/shared/directives/showtooltip.directive';
import { KeyboardShortcuts } from '@/shared/enums/keyboard-shortcuts.enum';

/**
 * SiteSelectionDropdownComponent
 *
 * Notes:
 * - The dropdown uses a single virtualized row stream so selected items and master
 *   items share the same CDK viewport for better performance on large selections.
 * - When search is active, the selected-items section is intentionally hidden to
 *   keep the UI focused on the filtered master list.
 *
 * Standalone searchable dropdown for sites or orgs with:
 * - single or multi select
 * - list view
 * - grouped view by organization
 * - virtual scrolling for large result sets
 * - sorting by recent, A-Z, or Z-A
 *
 * Group view behavior:
 * - Groups items by `groupedKey` (typically `orgName`).
 * - `Recently Created Sites` sorts organizations by the newest site in each group,
 *   and sorts sites inside each organization newest to oldest.
 * - `A-Z` and `Z-A` sort both organization labels and site labels alphabetically.
 *
 * Search:
 * - Search is applied before grouping.
 * - Search matches the primary label, secondary label, and address when `siteSelection` is enabled.
 *
 * Virtual scroll:
 * - A single `cdk-virtual-scroll-viewport` is shared by list and group views.
 * - Group mode renders a flattened sequence of group headers and item rows so CDK can virtualize it.
 */
@Component({
  selector: 'app-site-selection-dropdown',
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatIconModule,
    MatButtonModule,
    MatMenuModule,
    ScrollingModule,
    MatTooltipModule,
    OverlayModule,
    MatRadioModule,
    NgxSkeletonLoaderModule,
    MatCheckboxModule,
    ShowtooltipDirective
  ],
  templateUrl: './site-selection-dropdown.component.html',
  styleUrls: ['./site-selection-dropdown.component.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class SiteSelectionDropdownComponent {

  // Display / data inputs
  items = input<Record<string, any>[]>([]);
  config = input<SiteSelectionDropdownConfig>();
  noResultsLabel = input<string>('No results found');
  itemsLoading = input<boolean>(false);

  //Portals
  isFacilisight = input<boolean>(false);
  isInternal = input<boolean>(true);
  shortcutKey = input<string | null>(null);

  private readonly defaultConfig = {
    displayKey: 'label',
    groupedKey: '',
    valueKey: '',
    pinnedItemId: '',
    placeholder: 'Select',
    searchPlaceholder: 'Search',
    siteSelection: false,
    orgSelection: false,
    multiSelect: false,
    selectAllOption: false,
    selectAllByDefault: false,
    panelClass: '',
    customClass: '',
    itemSize: 28,
    visibleItemsCount: 12,
    bufferItemsCount: 10,
    virtualScrollThreshold: 20,
    disablekey: 'disabled',
    tooltipMessageKey: 'tooltipMessage',
    groupedItemTooltipMessage: '',
    enableChangeViewButton: true,
    remoteFilter: false
  };
  dropdownConfig = computed(() => ({ ...this.defaultConfig, ...this.config() }));
  isDisabledMode = computed(() => !!this.dropdownConfig().disabledMode);
  readonly maxViewportHeight = 480;
  viewportHeight = computed(() =>
    Math.min(
      Math.min(this.viewportRows().length, this.dropdownConfig().visibleItemsCount) * this.dropdownConfig().itemSize,
      this.maxViewportHeight
    )
  );
  overlayHeight = computed(() => {
    const minEmptyHeight = 160;
    const minLoadingHeight = 480;
    const remoteSelectedHeight = this.dropdownConfig().remoteFilter && this.selectedItemsFromCache().length
      ? this.dropdownConfig().itemSize
      : 0;

    if (this.itemsLoading()) {
      return minLoadingHeight;
    }

    const listHeight = this.useVirtualScroll()
      ? this.viewportHeight()
      : this.viewportRows().length * this.dropdownConfig().itemSize;

    if (!this.filteredItems().length) {
      return minEmptyHeight + remoteSelectedHeight;
    }

    return Math.min(listHeight + 70 + remoteSelectedHeight, this.maxViewportHeight);
  });

  // Component state
  value = model<any>(null);
  searchTerm = signal('');
  activeOptionKey = signal<string | null>(null);
  private lastActiveRowIndex = signal<number | null>(null);
  private lastInteractedSection = signal<'selected' | 'main' | null>(null);
  private selectedItemCache = signal<Record<string, any> | Record<string, any>[] | null>(null);
  selectionChange = output<Record<string, any> | Record<string, any>[] | null>();
  filterMode = output<any>();
  panelClosed = output<any>();

  viewMode = signal<'list' | 'group'>('list');
  sortMode = signal<'recent' | 'asc' | 'desc'>('recent');
  isSortMenuOpen = signal(false);
  isOpen = signal(false);
  private openedSelectionSnapshot: any = null;

  private searchSubject = new Subject<string>();
  private destroyRef = inject(DestroyRef);
  private hostElement = inject(ElementRef<HTMLElement>);
  private overlay = inject(Overlay);
  private keyboardShortcutService = inject(KeyboardShortcutService);
  private lastHandledShortcutSequence = this.keyboardShortcutService.shortcutEvent()?.sequence ?? 0;
  readonly scrollStrategy = this.overlay.scrollStrategies.reposition();
  private pendingArrowDelta = 0;
  private pendingArrowRepeat = false;
  private arrowNavigationFrameId: number | null = null;

  // Reference to the search input element
  @ViewChild('triggerInput') private triggerInput?: ElementRef<HTMLInputElement>;
  @ViewChild('searchInput') private searchInput?: ElementRef<HTMLInputElement>;
  @ViewChild('panelSurface') private panelSurface?: ElementRef<HTMLElement>;
  @ViewChild(CdkVirtualScrollViewport) private viewport?: CdkVirtualScrollViewport;

  constructor() {
    // Open visible org dropdowns on O key press.
    effect(() => {
      const shortcut = this.keyboardShortcutService.shortcutEvent();
      if (!shortcut || shortcut.sequence <= this.lastHandledShortcutSequence) {
        return;
      }
      this.lastHandledShortcutSequence = shortcut.sequence;

      untracked(() => {
        const configuredShortcutKey = this.shortcutKey();

        if (shortcut.command !== KEYBOARD_SHORTCUT_COMMANDS.OPEN_ORG_OR_SITE_SELECTION_DROPDOWN
          || !configuredShortcutKey
          || shortcut.key !== configuredShortcutKey
          || this.isDisabledMode()
          || this.isOpen()) {
          return;
        }

        const host = this.hostElement.nativeElement;
        // Open from shortcut only when this dropdown is actually visible and interactive.
        const hasClientRects = !!host.getClientRects().length;
        const isHiddenByTree = !!host.closest('[hidden], [inert]');
        const isVisibilityHidden = getComputedStyle(host).visibility === 'hidden';
        const hasBlockingOverlay = this.keyboardShortcutService.hasBlockingOverlay(host);

        if (!hasClientRects || isHiddenByTree || isVisibilityHidden || hasBlockingOverlay) {
          return;
        }

        this.openDropdown();
      });
    });

    // Auto-select everything when the config asks for it and the control is still empty.
    effect(() => {
      const config = this.dropdownConfig();
      const currentValue = this.value();

      if (!config.multiSelect || !config.selectAllByDefault) {
        return;
      }

      if (Array.isArray(currentValue) && currentValue.length) {
        return;
      }

      const allValues = this.items().map((item) => this.rawValueOf(item));
      if (allValues.length) {
        this.value.set(allValues);
      }
    });

    // Subscribe to search input changes with debounce and distinctUntilChanged
    this.searchSubject
      .pipe(debounceTime(200), distinctUntilChanged(), takeUntilDestroyed(this.destroyRef))
      .subscribe((term) => {
        if (this.dropdownConfig().remoteFilter) {
          this.filterMode.emit({
            searchText: term
          });
        } else {
          this.searchTerm.set(term);
        }
      });

    // Keep the selected cache in sync when the parent sets value/items before or after remote data arrives.
    effect(() => {
      if (!this.dropdownConfig().remoteFilter) {
        return;
      }

      const currentValue = this.value();
      if (currentValue === null || currentValue === undefined || currentValue === '') {
        this.selectedItemCache.set(null);
        return;
      }

      this.selectedItemCache.set(this.resolveSelection(currentValue));
    });

    // Keep active option aligned with visible filtered rows while preserving arrow-key movement.
    effect(() => {
      if (!this.isOpen()) {
        return;
      }

      const options = this.navigableOptions();
      untracked(() => this.syncActiveOptionWithRows(options));
    });
  }

  // Derived list items state
  filteredItems = computed(() => {
    const term = this.searchTerm().trim().toLowerCase();
    const list = this.sortItemsByDefault(this.items());
    if (!term) return list;
    const dKey = this.dropdownConfig().displayKey;
    const sKey = this.dropdownConfig().groupedKey;
    return list.filter((item) => {
      const primary = String(item[dKey] ?? '').toLowerCase();
      const secondary = sKey ? String(item[sKey] ?? '').toLowerCase() : '';
      const address = this.dropdownConfig().siteSelection ? String(item[this.dropdownConfig().addressKey ?? 'address'] ?? '').toLowerCase() : '';
      return primary.includes(term) || secondary.includes(term) || address.includes(term);
    });
  });

  /** Cached selected item(s) so remote filtering can keep showing them even if the current page changes. */
  selectedItemsFromCache = computed(() => {
    const cached = this.selectedItemCache();
    if (!cached) {
      return [];
    }

    return Array.isArray(cached) ? cached : [cached];
  });

  useVirtualScroll = computed(() => this.filteredItems().length > this.dropdownConfig().virtualScrollThreshold);

  groupedItems = computed(() => {
    const items = this.filteredItems();
    const itemsForGroups = this.excludePinnedItemFromGrouping(items);
    const groupedKey = this.dropdownConfig().groupedKey;

    if (this.viewMode() !== 'group' || !groupedKey) {
      return [];
    }

    const groups = new Map<string, Record<string, any>[]>();

    for (const item of itemsForGroups) {
      const groupLabel = String(item[groupedKey] ?? '').trim() || 'Ungrouped';
      const groupItems = groups.get(groupLabel) ?? [];
      groupItems.push(item);
      groups.set(groupLabel, groupItems);
    }

    const mode = this.sortMode();
    const displayKey = this.dropdownConfig().displayKey;

    return Array.from(groups.entries())
      .map(([label, groupItems]) => ({
        label,
        items: this.sortGroupItems(groupItems, mode, displayKey),
      }))
      .sort((a, b) => this.compareGroupLabels(a, b, mode));
  });

  groupedRows = computed(() => {
    return this.groupedItems().reduce<Array<
      { type: 'group'; label: string; items: Record<string, any>[] } |
      { type: 'item'; item: Record<string, any> }
    >>((rows, group) => {
      rows.push({ type: 'group', label: group.label, items: group.items });
      rows.push(...group.items.map((item) => ({ type: 'item' as const, item })));
      return rows;
    }, []);
  });

  /** Builds a single flattened row list for the viewport, including optional selected-section rows. */
  viewportRows = computed(() => {
    const rows: any[] = [];
    const collapseActive = this.dropdownConfig().selectAllOption && this.allItemsSelected();
    const hasSelectedSection = this.dropdownConfig().multiSelect && !this.searchTerm().trim() && this.filteredSelectedItems().length > 0 && !collapseActive;

    if (hasSelectedSection) {
      rows.push({ type: 'section-header' as const, label: 'Selected Sites' });

      if (this.viewMode() === 'group' && this.selectedGroupedRows().length) {
        rows.push(...this.selectedGroupedRows());
      } else {
        rows.push(...this.filteredSelectedItems());
      }

      rows.push({ type: 'section-divider' as const });
    }

    if (this.dropdownConfig().multiSelect && !collapseActive && this.selectedItemIds().length && !this.searchTerm()) {
      rows.push({ type: 'section-header' as const, label: 'All Sites' });
    }

    rows.push(...this.renderedRows());
    return rows;
  });

  /** Returns the height of the overlay when virtual scroll is not used. */
  overLayHeightWithoutVirtualScroll = computed(() => {
    const rows = this.viewportRows();
    return Math.min(rows.length * this.dropdownConfig().itemSize, 250);
  });

  /** Indicates whether the Selected Sites section is currently visible at the top of the list. */
  hasSelectedSection = computed(() => {
    const collapseActive = this.dropdownConfig().selectAllOption && this.allItemsSelected();
    return this.dropdownConfig().multiSelect && !this.searchTerm().trim() && this.filteredSelectedItems().length > 0 && !collapseActive;
  });

  renderedRows = computed(() => {
    const rows = this.viewMode() === 'group' && this.dropdownConfig().groupedKey
      ? this.groupedRows()
      : this.filteredItems();
    const pinnedRow = this.getPinnedGroupedRow();
    const rowsWithPinned = pinnedRow ? [pinnedRow, ...rows] : rows;

    if (this.shouldShowSelectAllOption()) {
      return [{ type: 'select-all' as const }, ...rowsWithPinned];
    }

    return rowsWithPinned;
  });

  /** Section of every viewport row, computed once per rows change (single pass). */
  private readonly rowSections = computed<Array<'selected' | 'main'>>(() => {
    const rows = this.viewportRows();
    const sections: Array<'selected' | 'main'> = new Array(rows.length);
    let inSelectedSection = false;

    for (let index = 0; index < rows.length; index++) {
      const row = rows[index];

      if (this.isSectionHeaderRow(row) && row.label === 'Selected Sites') {
        inSelectedSection = true;
      } else if (this.isSectionDividerRow(row)) {
        inSelectedSection = false;
      }

      sections[index] = inSelectedSection ? 'selected' : 'main';
    }

    return sections;
  });

  /** Maps option keys to their navigation index for quick lookup. */
  private readonly navigationIndexMap = computed(() => {
    const map = new Map<string, number>();

    this.navigableOptions().forEach((option, index) => {
      map.set(option.key, index);
    });

    return map;
  });

  /** Returns whether a group has at least one selected child. */
  isGroupPartiallySelected(group: any): boolean {
    if (!this.dropdownConfig().multiSelect) {
      return false;
    }

    const selectableItems = group.items.filter((item) => !this.isItemDisabled(item));
    if (!selectableItems.length) {
      return false;
    }

    const selectedKeys = this.selectedValueSet();
    const selectedCount = selectableItems.filter((item) => selectedKeys.has(this.selectionKey(this.rawValueOf(item)))).length;
    return selectedCount > 0 && selectedCount < selectableItems.length;
  }

  /** Returns whether all items in a group are selected. */
  isGroupFullySelected(group: any): boolean {
    if (!this.dropdownConfig().multiSelect) {
      return false;
    }

    const selectableItems = group.items.filter((item) => !this.isItemDisabled(item));
    if (!selectableItems.length) {
      return false;
    }

    const selectedKeys = this.selectedValueSet();
    return selectableItems.every((item) => selectedKeys.has(this.selectionKey(this.rawValueOf(item))));
  }

  /** Toggles all items in a group for multi-select group view. */
  toggleGroupSelection(group: any): void {
    if (!this.dropdownConfig().multiSelect) {
      return;
    }

    const selectableItems = group.items.filter((item) => !this.isItemDisabled(item));
    if (!selectableItems.length) {
      return;
    }

    const current = Array.isArray(this.value()) ? [...this.value()] : [];
    const allGroupValues = group.items.map((item) => this.rawValueOf(item));
    const groupValues = selectableItems.map((item) => this.rawValueOf(item));
    const currentKeys = new Set(current.map((entry) => this.selectionKey(entry)));
    const allGroupKeys = allGroupValues.map((value) => this.selectionKey(value));
    const groupKeys = groupValues.map((value) => this.selectionKey(value));
    const allSelected = groupKeys.every((key) => currentKeys.has(key));

    const next = allSelected
      ? current.filter((entry) => !allGroupKeys.includes(this.selectionKey(entry)))
      : [...current, ...groupValues].filter((entry, index, list) =>
        index === list.findIndex((value) => this.selectionKey(value) === this.selectionKey(entry))
      );

    this.onSelectionChange(next);
  }

  /** Resolves the raw `value()` back into full item(s) from the source list. */
  hasValue = computed(() => {
    const v = this.value();
    return v !== null && v !== undefined && v !== '';
  });

  /** What the closed trigger shows, e.g. "WeWork GTC 1 Bellandur (+3 more)". */
  triggerLabel = computed(() => {
    const value = this.value();

    if (value == null) {
      return '';
    }

    const valueKey = this.dropdownConfig().valueKey;
    const items = this.dropdownConfig().remoteFilter
      ? [...this.items(), ...this.selectedItemsFromCache()]
      : this.items();

    // For Multi-select, show the first selected item and a count of the rest.
    if (this.dropdownConfig().multiSelect) {
      const selectedKeys = new Set(value.map((v: any) => this.selectionKey(v)));
      const selectedItems = items.filter(item => selectedKeys.has(this.selectionKey(valueKey ? item[valueKey] : item)));

      if (!selectedItems.length) {
        return '';
      }

      if (this.dropdownConfig().selectAllOption && this.items().length > 0 && selectedItems.length === this.items().length) {
        return 'All Sites';
      }

      return this.formatMultiSelectTriggerLabel(selectedItems);
    }

    const selectedItem = items.find(item =>
      (valueKey ? item[valueKey] : item) === value
    );

    return selectedItem
      ? this.getPrimaryLabel(selectedItem)
      : '';
  });

  // ---------------------------------------------------------------------
  // Template helpers
  // ---------------------------------------------------------------------
  /** Returns a stable track id for a raw item row. */
  trackByFn = (_index: number, item: Record<string, any>): unknown => {
    const vKey = this.dropdownConfig().valueKey;
    return vKey ? item[vKey] : item;
  };

  /** Returns whether the item is disabled by the configured disable key. */
  isItemDisabled(item: Record<string, any>): boolean {
    const disableKey = this.dropdownConfig().disablekey;
    return !!disableKey && !!item?.[disableKey];
  }

  /** Returns whether the item should be blocked from user selection interactions. */
  isItemSelectionBlocked(item: Record<string, any>): boolean {
    return this.isItemDisabled(item) && !this.isItemSelected(this.rawValueOf(item));
  }

  /** Returns the configured tooltip text for a disabled item. */
  getItemTooltipMessage(item: Record<string, any>): string {
    const tooltipKey = this.dropdownConfig().tooltipMessageKey;
    return tooltipKey ? String(item?.[tooltipKey] ?? '') : '';
  }

  /** Returns whether the item should show a disabled tooltip. */
  getItemTooltip(item: Record<string, any>): string | null {
    return this.isItemDisabled(item) ? this.getItemTooltipMessage(item) : null;
  }

  /** Returns tooltip text only when selection is blocked for the item. */
  getSelectionBlockedTooltip(item: Record<string, any>): string | null {
    return this.isItemSelectionBlocked(item) ? this.getItemTooltipMessage(item) : null;
  }

  /** Stable key used for option identity and active-row state. */
  optionKeyOf(item: Record<string, any>): string {
    return this.selectionKey(this.rawValueOf(item));
  }

  /** Returns whether an item row is the currently keyboard-active option. */
  isActiveOption(item: Record<string, any>, rowIndex: number): boolean {
    return this.activeOptionKey() === this.itemOptionKey(item, rowIndex);
  }

  /** Returns whether the select-all row is the currently keyboard-active option. */
  isActiveSelectAllOption(rowIndex: number): boolean {
    return this.activeOptionKey() === this.selectAllOptionKey(rowIndex);
  }

  /** Stable key used for the synthetic select-all row in keyboard navigation. */
  selectAllOptionKey(rowIndex: number): string {
    return `${this.rowSectionKey(rowIndex)}-select-all`;
  }

  /** Returns whether a group row is the currently keyboard-active option. */
  isActiveGroupOption(group: { type: 'group'; label: string }, rowIndex: number): boolean {
    return this.activeOptionKey() === this.groupOptionKey(group, rowIndex);
  }

  /** Stable key used for a group header row in keyboard navigation. */
  groupOptionKey(group: { label: string }, rowIndex: number): string {
    return `${this.rowSectionKey(rowIndex)}-group-${group.label ?? ''}`;
  }

  /** Stable key used for an item row in keyboard navigation (row-aware to avoid duplicates across sections). */
  itemOptionKey(item: Record<string, any>, rowIndex: number): string {
    return `${this.rowSectionKey(rowIndex)}-item-${this.optionKeyOf(item)}`;
  }

  /** Returns which visual section a viewport row belongs to (O(1)). */
  private rowSectionKey(rowIndex: number): 'selected' | 'main' {
    return this.rowSections()[rowIndex] ?? 'main';
  }

  /** Returns whether a group header should be disabled because all children are disabled. */
  isGroupSelectionBlocked(group: any): boolean {
    return !!group?.items?.length && group.items.every((item: Record<string, any>) => this.isItemDisabled(item));
  }

  /** Returns whether group-parent disabled UI should be active (multi-select only). */
  isGroupParentDisabled(group: any): boolean {
    return this.dropdownConfig().multiSelect && this.isGroupSelectionBlocked(group);
  }

  /** Returns tooltip text for blocked group headers. */
  getGroupSelectionBlockedTooltip(group: any): string | null {
    if (!this.isGroupParentDisabled(group)) {
      return null;
    }

    return this.dropdownConfig().groupedItemTooltipMessage || null;
  }

  /** Returns a stable track id for grouped rows and delegates item rows to `trackByFn`. */
  trackByRenderedRow = (_index: number, row: any): unknown => {
    if (this.isSelectAllRow(row)) {
      return 'select-all-option';
    }

    if (this.isPinnedRow(row)) {
      return `pinned-${this.selectionKey(this.rawValueOf(row.item))}`;
    }

    if (this.isSectionHeaderRow(row)) {
      return `section-header-${row.label ?? ''}`;
    }

    if (this.isSectionDividerRow(row)) {
      return 'section-divider';
    }

    if (this.isGroupRow(row)) {
      return `group-${row.label ?? ''}`;
    }

    return this.trackByFn(0, row);
  };

  /** Narrows a rendered row to a group header row. */
  isGroupRow(row: any): row is { type: 'group'; label: string } {
    return !!row && typeof row === 'object' && row.type === 'group';
  }

  /** Narrows a rendered row to the synthetic select-all row. */
  isSelectAllRow(row: any): row is { type: 'select-all' } {
    return !!row && typeof row === 'object' && row.type === 'select-all';
  }

  /** Narrows a rendered row to a section header row. */
  isSectionHeaderRow(row: any): row is { type: 'section-header'; label: string } {
    return !!row && typeof row === 'object' && row.type === 'section-header';
  }

  /** Narrows a rendered row to a section divider row. */
  isSectionDividerRow(row: any): row is { type: 'section-divider' } {
    return !!row && typeof row === 'object' && row.type === 'section-divider';
  }

  /** Narrows a rendered row to the synthetic pinned row used in grouped single-select mode. */
  isPinnedRow(row: any): row is { type: 'pinned-item'; item: Record<string, any> } {
    return !!row && typeof row === 'object' && row.type === 'pinned-item' && !!row.item;
  }

  /** compareWith for mat-select so object-valued items match by identity/valueKey. */
  compareFn = (a: any, b: any): boolean => {
    if (a === b) return true;
    if (a == null || b == null) return false;
    return JSON.stringify(a) === JSON.stringify(b);
  };

  rawValueOf = (item: Record<string, any>): unknown => {
    const vKey = this.dropdownConfig().valueKey;
    return vKey ? item[vKey] : item;
  };

  /** Returns the current selected raw values as an array for template checks. */
  selectedItemIds = computed(() => {
    const value = this.value();

    if (this.dropdownConfig().multiSelect) {
      return Array.isArray(value) ? value : [];
    }

    return value == null ? [] : [value];
  });

  /** Fast lookup set for multi-select checkbox rendering. */
  selectedItemIdSet = computed(() => new Set(this.selectedItemIds().map((item) => this.selectionKey(item))));

  /** Fast lookup set for raw selected values. */
  selectedValueSet = computed(() => new Set(this.selectedItemIds().map((item) => this.selectionKey(item))));

  /** Returns the selected items in the same shape as the master list. */
  selectedItems = computed(() => {
    if (!this.dropdownConfig().multiSelect) {
      return [];
    }

    const items = this.sortItemsByDefault(this.dropdownConfig().remoteFilter ? [...this.items(), ...this.selectedItemsFromCache()] : this.items());
    const selectedKeys = this.selectedValueSet();
    return items.filter((item) => selectedKeys.has(this.selectionKey(this.rawValueOf(item))));
  });

  /** Returns the selected items after the current search filter is applied. */
  filteredSelectedItems = computed(() => {
    const selected = this.selectedItems();
    const visibleIds = new Set(this.filteredItems().map((item) => this.selectionKey(this.rawValueOf(item))));

    return selected.filter((item) => visibleIds.has(this.selectionKey(this.rawValueOf(item))));
  });

  /** Grouped view rows for the selected-items section. */
  selectedGroupedRows = computed(() => {
    if (this.viewMode() !== 'group' || !this.dropdownConfig().groupedKey) {
      return [];
    }

    const groups = new Map<string, Record<string, any>[]>();
    const groupedKey = this.dropdownConfig().groupedKey;
    const mode = this.sortMode();
    const displayKey = this.dropdownConfig().displayKey;

    for (const item of this.filteredSelectedItems()) {
      const groupLabel = String(item[groupedKey] ?? '').trim() || 'Ungrouped';
      const groupItems = groups.get(groupLabel) ?? [];
      groupItems.push(item);
      groups.set(groupLabel, groupItems);
    }

    return Array.from(groups.entries())
      .map(([label, groupItems]) => ({
        label,
        items: this.sortGroupItems(groupItems, mode, displayKey),
      }))
      .sort((a, b) => this.compareGroupLabels(a, b, mode))
      .reduce<Array<
        { type: 'group'; label: string; items: Record<string, any>[] } |
        { type: 'item'; item: Record<string, any> }
      >>((rows, group) => {
        rows.push({ type: 'group', label: group.label, items: group.items });
        rows.push(...group.items.map((item) => ({ type: 'item' as const, item })));
        return rows;
      }, []);
  });

  /** Flat list of keyboard-navigable options from the currently filtered/rendered rows. */
  navigableOptions = computed(() => {
    const rows = this.viewportRows();
    const sections = this.rowSections();
    const multiSelect = this.dropdownConfig().multiSelect;
    const isGroupView = this.viewMode() === 'group';
    const options: NavigableOption[] = [];

    for (let rowIndex = 0; rowIndex < rows.length; rowIndex++) {
      const row = rows[rowIndex];
      const section = sections[rowIndex];

      if (this.isSelectAllRow(row) && multiSelect) {
        options.push({
          key: this.selectAllOptionKey(rowIndex),
          rowIndex,
          section,
          kind: 'select-all',
        });
        continue;
      }

      if (this.isGroupRow(row) && multiSelect && isGroupView) {
        options.push({
          key: this.groupOptionKey(row, rowIndex),
          rowIndex,
          section,
          kind: 'group',
          group: row,
        });
        continue;
      }

      const item = this.getRowItem(row);
      if (!item) {
        continue;
      }

      options.push({
        key: this.itemOptionKey(item, rowIndex),
        rowIndex,
        section,
        kind: 'item',
        item,
      });
    }

    return options;
  });

  /** Returns whether every currently visible item is selected. */
  allItemsSelected = computed(() => {
    const items = this.filteredItems();
    return items.length > 0 && items.every((item) => this.isItemSelected(this.rawValueOf(item)));
  });

  /** Returns whether some, but not all, currently visible items are selected. */
  someItemsSelected = computed(() => {
    const items = this.filteredItems();
    const selectedCount = items.filter((item) => this.isItemSelected(this.rawValueOf(item))).length;
    return selectedCount > 0 && selectedCount < items.length;
  });

  /** Builds a stable string key for selection comparisons and set membership. */
  private selectionKey(value: unknown): string {
    if (typeof value === 'string') {
      return value;
    }

    const serialized = JSON.stringify(value);
    return serialized ?? String(value);
  }

  /** Checks whether a raw value is currently selected. */
  isItemSelected(value: unknown): boolean {
    return this.selectedItemIdSet().has(this.selectionKey(value));
  }

  /** Reads the configured primary label field from an item. */
  getPrimaryLabel(item: Record<string, any>): string {
    return String(item[this.dropdownConfig().displayKey] ?? '');
  }

  /** Reads the configured secondary label field from an item, if present. */
  getSecondaryLabel(item: Record<string, any>): string {
    const sKey = this.dropdownConfig().groupedKey;
    return sKey ? String(item[sKey] ?? '') : '';
  }

  /** Formats the closed trigger label for multi-select mode. */
  private formatMultiSelectTriggerLabel(selectedItems: Record<string, any>[]): string {
    const first = this.getPrimaryLabel(selectedItems[0]);
    const maxLabelLength = 18;
    const truncated = first.length > maxLabelLength
      ? `${first.slice(0, maxLabelLength - 3).replace(/\s+$/, '')}...`
      : first;

    return selectedItems.length === 1
      ? first
      : `${truncated} & +${selectedItems.length - 1}`;
  }

  /** Sorts the base item list according to the active site-selection mode. */
  private sortItemsByDefault(items: Record<string, any>[]): Record<string, any>[] {

    const mode = this.sortMode();
    const displayKey = this.dropdownConfig().displayKey;

    return [...items].sort((a, b) => {
      const pinnedOrder = this.comparePinnedItemPriority(a, b);
      if (pinnedOrder !== 0) {
        return pinnedOrder;
      }

      if (mode === 'recent') {
        const dateTimeKey = this.getDateTimeKey();
        const aTime = this.parseDateTime(a?.[dateTimeKey]);
        const bTime = this.parseDateTime(b?.[dateTimeKey]);
        return bTime - aTime;
      }

      const aLabel = String(a?.[displayKey] ?? '').toLowerCase();
      const bLabel = String(b?.[displayKey] ?? '').toLowerCase();

      return mode === 'asc'
        ? aLabel.localeCompare(bLabel)
        : bLabel.localeCompare(aLabel);
    });
  }

  /** Sorts items inside a group using the active sort mode. */
  private sortGroupItems(
    items: Record<string, any>[],
    mode: 'recent' | 'asc' | 'desc',
    displayKey: string
  ): Record<string, any>[] {
    return [...items].sort((a, b) => {
      const pinnedOrder = this.comparePinnedItemPriority(a, b);
      if (pinnedOrder !== 0) {
        return pinnedOrder;
      }

      if (mode === 'recent') {
        const dateTimeKey = this.getDateTimeKey();
        return this.parseDateTime(b?.[dateTimeKey]) - this.parseDateTime(a?.[dateTimeKey]);
      }

      const aLabel = String(a?.[displayKey] ?? '').toLowerCase();
      const bLabel = String(b?.[displayKey] ?? '').toLowerCase();
      return mode === 'asc'
        ? aLabel.localeCompare(bLabel)
        : bLabel.localeCompare(aLabel);
    });
  }

  /** Orders groups by newest item or alphabetically based on the active sort mode. */
  private compareGroupLabels(
    a: { label: string; items: Record<string, any>[] },
    b: { label: string; items: Record<string, any>[] },
    mode: 'recent' | 'asc' | 'desc'
  ): number {
    const pinnedGroupOrder = this.comparePinnedGroupPriority(a.items, b.items);
    if (pinnedGroupOrder !== 0) {
      return pinnedGroupOrder;
    }

    if (mode === 'recent') {
      const dateTimeKey = this.getDateTimeKey();
      const aTime = Math.max(...a.items.map((item) => this.parseDateTime(item?.[dateTimeKey])), 0);
      const bTime = Math.max(...b.items.map((item) => this.parseDateTime(item?.[dateTimeKey])), 0);
      return bTime - aTime;
    }

    return mode === 'asc'
      ? a.label.localeCompare(b.label)
      : b.label.localeCompare(a.label);
  }

  /** Converts a date-like value into a comparable timestamp. */
  private parseDateTime(value: unknown): number {
    if (!value) {
      return 0;
    }

    const time = new Date(value as string).getTime();
    return Number.isFinite(time) ? time : 0;
  }

  /** Returns whether pinning should be applied for single-select mode. */
  private shouldPinSingleSelectItem(): boolean {
    return !this.dropdownConfig().multiSelect && !!this.dropdownConfig().pinnedItemId;
  }

  /** Returns whether an item matches the configured pinned item id. */
  private isPinnedItem(item: Record<string, any>): boolean {
    if (!this.shouldPinSingleSelectItem()) {
      return false;
    }

    const pinnedKey = this.selectionKey(this.dropdownConfig().pinnedItemId);
    const itemKey = this.selectionKey(this.rawValueOf(item));
    return pinnedKey === itemKey;
  }

  /** Gives pinned item absolute priority over normal sorting. */
  private comparePinnedItemPriority(a: Record<string, any>, b: Record<string, any>): number {
    const aPinned = this.isPinnedItem(a);
    const bPinned = this.isPinnedItem(b);

    if (aPinned === bPinned) {
      return 0;
    }

    return aPinned ? -1 : 1;
  }

  /** Returns the standalone pinned row for grouped single-select mode. */
  private getPinnedGroupedRow(): { type: 'pinned-item'; item: Record<string, any> } | null {
    if (this.viewMode() !== 'group' || !this.dropdownConfig().groupedKey || !this.shouldPinSingleSelectItem()) {
      return null;
    }

    const pinnedItem = this.filteredItems().find((item) => this.isPinnedItem(item));
    return pinnedItem ? { type: 'pinned-item' as const, item: pinnedItem } : null;
  }

  /** Excludes pinned item from grouped buckets so it does not render under an Ungrouped header. */
  private excludePinnedItemFromGrouping(items: Record<string, any>[]): Record<string, any>[] {
    if (this.viewMode() !== 'group' || !this.dropdownConfig().groupedKey || !this.shouldPinSingleSelectItem()) {
      return items;
    }

    return items.filter((item) => !this.isPinnedItem(item));
  }

  /** Places the group containing the pinned item before all other groups. */
  private comparePinnedGroupPriority(aItems: Record<string, any>[], bItems: Record<string, any>[]): number {
    if (!this.shouldPinSingleSelectItem()) {
      return 0;
    }

    const aHasPinned = aItems.some((item) => this.isPinnedItem(item));
    const bHasPinned = bItems.some((item) => this.isPinnedItem(item));

    if (aHasPinned === bHasPinned) {
      return 0;
    }

    return aHasPinned ? -1 : 1;
  }

  /** Resolves the configured datetime key, falling back to the legacy field name. */
  private getDateTimeKey(): string {
    return this.dropdownConfig().dateTimeKey || 'createdDateTime';
  }

  // Event handlers
  /** Queues search input updates so filtering can be debounced. */
  onSearchInput(term: string): void {
    this.searchSubject.next(term);
  }

  /** Clears the current search term and resets the debounced search stream. */
  clearSearch(): void {
    this.searchTerm.set('');
    if (this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.value = '';
    }
    this.searchSubject.next('');
  }

  /** Stores the new selection and emits the resolved full item(s). */
  onSelectionChange(selected: any): void {
    this.value.set(selected);
    this.selectedItemCache.set(this.resolveSelection(selected));
    const resolved = this.resolveSelection(selected);
    this.selectionChange.emit(resolved);
  }

  /** Clears the current selection without collapsing the parent click target. */
  clearSelection(event: Event): void {
    event.stopPropagation();
    this.value.set(null);
    this.selectedItemCache.set(null);
    this.selectionChange.emit(null);
  }

  /** Toggles between opening and closing the dropdown panel. */
  toggleDropdown(): void {
    if (this.isDisabledMode()) {
      return;
    }

    this.isOpen() ? this.closeDropdown() : this.openDropdown();
  }

  /**
   * Opens the dropdown panel if it is not already open.
   *
   * For multi-select keyboard flows, capture current selection so
   * Escape/Tab can revert in-panel edits as a cancel action.
   */
  openDropdown(): void {
    if (this.isDisabledMode() || this.isOpen()) return;
    this.openedSelectionSnapshot = this.cloneSelectionValue(this.value());
    this.isOpen.set(true);
  }

  /**
   * Closes the dropdown panel and clears transient UI state.
   *
   * The selection payload is resolved before reset so `panelClosed` always
   * emits the committed selection visible at close time.
   */
  closeDropdown(): void {
    if (!this.isOpen()) return;
    const selectionOnClose = this.resolveSelection(this.value());
    this.resetPendingArrowNavigation();
    this.isOpen.set(false);
    this.activeOptionKey.set(null);
    this.lastActiveRowIndex.set(null);
    this.lastInteractedSection.set(null);
    this.clearSearch();
    this.resetFiltersAndSort();
    this.panelClosed.emit(selectionOnClose);
    this.openedSelectionSnapshot = null;
  }

  /** Reverts keyboard-driven multi-select edits made in this open session and closes. */
  private cancelKeyboardSelectionChanges(): void {
    const revertValue = this.cloneSelectionValue(this.openedSelectionSnapshot);
    this.value.set(revertValue ?? null);

    const resolved = this.resolveSelection(revertValue);
    this.selectedItemCache.set(resolved);
    this.selectionChange.emit(resolved);

    this.closeDropdown();
  }

  /** Runs the post-attach focus and scroll helper when the overlay becomes visible. */
  onOverlayAttached(): void {
    this.autoScrollToSelectedValue();
  }

  /** Focuses the search input and scrolls the selected row into view. */
  autoScrollToSelectedValue(): void {
    setTimeout(() => {
      requestAnimationFrame(() => {
        this.searchInput?.nativeElement.focus({ preventScroll: true });
        if (!this.dropdownConfig().multiSelect) {
          this.scrollSelectedIntoView();
        }
      });
    });
  }

  /** Closes the panel when the backdrop is clicked. */
  onBackdropClick(): void {
    this.closeDropdown();
  }

  /** Returns whether the key event originated from an input-like field. */
  private isEventFromInputField(event: KeyboardEvent): boolean {
    const target = event.target as HTMLElement | null;

    if (!target) {
      return false;
    }

    const tagName = target.tagName;
    return tagName === 'INPUT' || tagName === 'TEXTAREA' || target.isContentEditable;
  }

  /**
   * Handles keyboard interaction for the open dropdown.
   *
   * - Arrow keys move active option
   * - Enter/Space toggles active option
   * - Shift+Enter applies and closes in multi-select
   * - Escape/Tab cancels and reverts in multi-select, closes directly in single-select
   */
  onDropdownKeydown(event: KeyboardEvent): void {
    if (!this.isOpen()) {
      return;
    }

    // Ignore key events originating from input-like fields, except for arrow keys which are used for navigation.
    if (
      this.isEventFromInputField(event) &&
      event.key !== KeyboardShortcuts.ARROW_DOWN &&
      event.key !== KeyboardShortcuts.ARROW_UP && 
      event.key !== KeyboardShortcuts.TAB && 
      event.key !== KeyboardShortcuts.ESCAPE
    ) {
      return;
    }

    // Keep keyboard events on the overlay panel only for keys handled by this dropdown.
    if (document.activeElement === this.searchInput?.nativeElement) {
      this.searchInput.nativeElement.blur();
      this.panelSurface?.nativeElement.focus({ preventScroll: true });
    }

    if (event.key === KeyboardShortcuts.ARROW_DOWN) {
      event.preventDefault();
      event.stopPropagation();
      this.queueArrowNavigation(1, event.repeat);
      return;
    }

    if (event.key === KeyboardShortcuts.ARROW_UP) {
      event.preventDefault();
      event.stopPropagation();
      this.queueArrowNavigation(-1, event.repeat);
      return;
    }

    if (event.key === KeyboardShortcuts.ENTER && event.shiftKey && this.dropdownConfig().multiSelect) {
      event.preventDefault();
      event.stopPropagation();
      this.closeDropdown();
      return;
    }

    if (event.key === KeyboardShortcuts.ENTER) {
      event.preventDefault();
      event.stopPropagation();
      this.selectActiveOption();
      return;
    }

    if (event.key === KeyboardShortcuts.SPACE && this.dropdownConfig().multiSelect && !event.shiftKey) {
      event.preventDefault();
      event.stopPropagation();
      this.selectActiveOption();
      return;
    }

    if (event.key === KeyboardShortcuts.ESCAPE || event.key === KeyboardShortcuts.TAB || (event.shiftKey && event.key === KeyboardShortcuts.TAB)) {
      event.preventDefault();
      event.stopPropagation();
      if (this.isOpen()) {
        if (this.dropdownConfig().multiSelect) {
          this.cancelKeyboardSelectionChanges();
        } else {
          this.closeDropdown();
        }
      }
    }
  }

  /** Coalesces rapid ArrowUp/ArrowDown key repeats into one update per frame. */
  private queueArrowNavigation(step: 1 | -1, isRepeat = false): void {
    this.pendingArrowDelta += step;
    this.pendingArrowRepeat = this.pendingArrowRepeat || isRepeat;

    if (this.arrowNavigationFrameId !== null) {
      return;
    }

    this.arrowNavigationFrameId = requestAnimationFrame(() => {
      const delta = this.pendingArrowDelta;
      const rapidNavigation = this.pendingArrowRepeat || Math.abs(delta) > 1;
      this.pendingArrowDelta = 0;
      this.pendingArrowRepeat = false;
      this.arrowNavigationFrameId = null;

      if (!delta || !this.isOpen()) {
        return;
      }

      const direction: 1 | -1 = delta > 0 ? 1 : -1;
      const maxStepsPerFrame = 5;
      const steps = Math.min(Math.abs(delta), maxStepsPerFrame);

      this.moveActiveOption(direction, rapidNavigation, steps);
    });
  }

  /** Clears queued arrow navigation work when the panel closes. */
  private resetPendingArrowNavigation(): void {
    this.pendingArrowDelta = 0;
    this.pendingArrowRepeat = false;

    if (this.arrowNavigationFrameId === null) {
      return;
    }

    cancelAnimationFrame(this.arrowNavigationFrameId);
    this.arrowNavigationFrameId = null;
  }

  /** Creates a lightweight copy of selection values for open-session snapshots. */
  private cloneSelectionValue(value: any): any {
    if (Array.isArray(value)) {
      return [...value];
    }

    return value;
  }

  /** Handles clicks on an option and updates selection state accordingly. */
  onOptionSelect(item: Record<string, any>): void {
    const rawValue = this.rawValueOf(item);

    if (this.isItemDisabled(item)) {
      if (!this.isItemSelected(rawValue)) {
        return;
      }

      if (this.dropdownConfig().multiSelect) {
        const current = Array.isArray(this.value()) ? [...this.value()] : [];
        const next = current.filter((entry) => !this.compareFn(entry, rawValue));
        this.onSelectionChange(next);
      } else if (this.compareFn(this.value(), rawValue)) {
        this.onSelectionChange(null);
      }
      return;
    }

    if (this.dropdownConfig().multiSelect) {
      const current = Array.isArray(this.value()) ? [...this.value()] : [];
      const index = current.findIndex((entry) => this.compareFn(entry, rawValue));
      if (index >= 0) {
        current.splice(index, 1);
      } else {
        current.push(rawValue);
      }
      this.onSelectionChange(current);
      return;
    }

    this.onSelectionChange(rawValue);
    this.closeDropdown();
  }

  /** Handles clicks on the synthetic select-all row in multi-select mode. */
  onSelectAllOptionSelect(): void {
    if (!this.dropdownConfig().multiSelect) {
      return;
    }

    const visibleValues = this.filteredItems().map((item) => this.rawValueOf(item));
    const current = Array.isArray(this.value()) ? [...this.value()] : [];
    const visibleKeys = new Set(visibleValues.map((value) => this.selectionKey(value)));
    const currentKeys = new Set(current.map((value) => this.selectionKey(value)));
    const allVisibleSelected = [...visibleKeys].every((key) => currentKeys.has(key));

    const next = allVisibleSelected
      ? current.filter((entry) => !visibleKeys.has(this.selectionKey(entry)))
      : [...current, ...visibleValues].filter((entry, index, list) =>
        index === list.findIndex((value) => this.selectionKey(value) === this.selectionKey(entry))
      );

    this.onSelectionChange(next);
  }

  /** Moves the active option by `count` steps in a single update. */
  private moveActiveOption(step: 1 | -1, rapidNavigation = false, count = 1): void {
    const options = this.navigableOptions();
    const multiSelect = this.dropdownConfig().multiSelect;

    if (!options.length) {
      this.activeOptionKey.set(null);
      this.lastActiveRowIndex.set(null);
      this.lastInteractedSection.set(null);
      return;
    }

    let currentKey = this.activeOptionKey();
    let remaining = count;

    // First press only activates an option (no movement), same as before.
    if (!currentKey) {
      const preferredSection = multiSelect ? this.lastInteractedSection() : null;
      const hasSelectAllOption = multiSelect && options.some((option) => option.kind === 'select-all');
      const hasSelectedSectionOptions = multiSelect && options.some((option) => option.section === 'selected');
      const resolvedPreferredSection = preferredSection
        ?? (step > 0 && hasSelectAllOption ? 'main' : null)
        ?? (step > 0 && hasSelectedSectionOptions ? 'selected' : null);

      currentKey = this.initialActiveOptionKey(options, false, resolvedPreferredSection);
      remaining -= 1;
    }

    const currentIndex = this.navigationIndexMap().get(currentKey) ?? -1;
    const baseIndex = currentIndex < 0 ? (step > 0 ? -1 : options.length) : currentIndex;
    const nextIndex = Math.max(0, Math.min(baseIndex + step * remaining, options.length - 1));
    const nextOption = options[nextIndex];

    if (multiSelect) {
      this.lastInteractedSection.set(nextOption.section);
    }

    this.lastActiveRowIndex.set(nextOption.rowIndex);
    this.activeOptionKey.set(nextOption.key);
    this.maybeScrollActiveOptionIntoView(nextOption, step, rapidNavigation);
  }

  /** Returns the starting keyboard-active key for panel open or arrow-key fallback. */
  private initialActiveOptionKey(
    options: NavigableOption[],
    preferSelectedSectionOnOpen: boolean,
    preferredSection?: 'selected' | 'main' | null
  ): string {
    if (preferSelectedSectionOnOpen && this.hasSelectedSection()) {
      return options[0].key;
    }

    if (this.dropdownConfig().multiSelect) {
      const sectionPreferredKey = this.firstOptionKeyInSection(options, preferredSection);
      if (sectionPreferredKey) {
        return sectionPreferredKey;
      }

      const mainSelectAllOption = options.find((option) => option.section === 'main' && option.kind === 'select-all');
      if (mainSelectAllOption) {
        return mainSelectAllOption.key;
      }
    }

    const selectedOptionKey = this.selectedNavigableOptionKey(options);

    // In grouped multi-select, prefer the currently selected item so Arrow navigation
    // continues from an actual selected row instead of jumping to a group header.
    if (this.dropdownConfig().multiSelect && this.viewMode() === 'group' && selectedOptionKey) {
      return selectedOptionKey;
    }

    if (this.dropdownConfig().multiSelect) {
      if (this.viewMode() === 'group') {
        const mainGroupOption = options.find((option) => option.section === 'main' && option.kind === 'group');
        if (mainGroupOption) {
          return mainGroupOption.key;
        }

        const firstGroupOption = options.find((option) => option.kind === 'group');
        if (firstGroupOption) {
          return firstGroupOption.key;
        }
      }

      const mainItemOption = options.find((option) => option.section === 'main' && option.kind === 'item');
      if (mainItemOption) {
        return mainItemOption.key;
      }
    }

    if (selectedOptionKey) {
      return selectedOptionKey;
    }

    const firstItemOption = options.find((option) => option.kind === 'item');
    return firstItemOption?.key ?? options[0].key;
  }

  /** Scrolls only when keyboard navigation moves the active option outside the visible viewport. */
  private maybeScrollActiveOptionIntoView(option: NavigableOption, step: 1 | -1, rapidNavigation = false): void {
    const rowIndex = option.rowIndex;

    if (rowIndex < 0) {
      return;
    }

    if (this.useVirtualScroll()) {
      const viewport = this.viewport;
      if (!viewport) {
        return;
      }

      const itemSize = this.dropdownConfig().itemSize;
      const scrollTop = viewport.measureScrollOffset('top');
      const viewportSize = viewport.getViewportSize();

      if (!itemSize || !viewportSize) {
        return;
      }

      const visibleTop = scrollTop;
      const visibleBottom = scrollTop + viewportSize;
      const itemTop = rowIndex * itemSize;
      const itemBottom = itemTop + itemSize;

      // Scroll only when keyboard focus crosses true pixel bounds.
      const shouldScrollDown = step > 0 && itemTop >= visibleBottom;
      const shouldScrollUp = step < 0 && itemTop < visibleTop;

      if (shouldScrollDown) {
        this.viewport?.scrollToOffset(Math.max(itemBottom - viewportSize, 0));
      } else if (shouldScrollUp) {
        this.viewport?.scrollToOffset(Math.max(itemTop, 0));
      }
      return;
    }

    const panel = document.querySelector('.site-selection-dropdown-panel .site-selection-dropdown-panel-surface') as HTMLElement | null;
    if (!panel) {
      return;
    }

    const optionElements = panel.querySelectorAll<HTMLElement>('[data-site-selection-option][data-option-key]');
    const optionElement = Array.from(optionElements).find((entry) => entry.getAttribute('data-option-key') === option.key) ?? null;
    if (!optionElement) {
      return;
    }

    const panelRect = panel.getBoundingClientRect();
    const optionRect = optionElement.getBoundingClientRect();
    const isAboveView = optionRect.top < panelRect.top;
    const isBelowView = optionRect.bottom > panelRect.bottom;

    if (isAboveView || isBelowView) {
      optionElement.scrollIntoView({ behavior: rapidNavigation ? 'auto' : 'smooth', block: 'nearest' });
    }
  }

  /** Selects or toggles the active option, preserving single/multi mode behavior. */
  private selectActiveOption(): void {
    const activeKey = this.activeOptionKey();
    if (!activeKey) {
      return;
    }

    const option = this.navigableOptions().find((entry) => entry.key === activeKey);
    if (!option) {
      return;
    }

    if (this.dropdownConfig().multiSelect) {
      this.lastInteractedSection.set(option.section);
    }

    if (option.kind === 'select-all') {
      this.onSelectAllOptionSelect();
      return;
    }

    if (option.kind === 'group' && option.group) {
      if (!this.isGroupParentDisabled(option.group)) {
        this.toggleGroupSelection(option.group);
      }
      return;
    }

    if (option.item) {
      this.onOptionSelect(option.item);
    }
  }

  /** Scrolls the selected item into view in either virtual or standard mode. */
  private scrollSelectedIntoView(): void {
    const filtered = this.filteredItems();
    if (!filtered.length) return;

    const selectedValue = this.value();
    if (selectedValue === null || selectedValue === undefined || selectedValue === '') {
      return;
    }

    const selectedIndex = this.findFilteredIndex(selectedValue);

    if (selectedIndex < 0) return;

    if (this.useVirtualScroll()) {
      this.viewport?.checkViewportSize();

      if (this.viewMode() === 'group') {
        const rowIndex = this.findRenderedRowIndex(selectedValue);
        if (rowIndex >= 0) {
          const topPaddingRows = 2;
          this.scrollVirtualViewportToIndex(Math.max(rowIndex - topPaddingRows, 0));
        }
      } else {
        this.scrollVirtualViewportToIndex(selectedIndex);
      }
      return;
    }

    const panel = document.querySelector('.site-selection-dropdown-panel .site-selection-dropdown-panel-surface') as HTMLElement | null;
    const option = panel?.querySelectorAll('[data-site-selection-option]')?.item(selectedIndex) as HTMLElement | null;
    option?.scrollIntoView({
      behavior: 'smooth',
      block: selectedIndex === filtered.length - 1 ? 'end' : 'nearest',
    });
  }

  /** Scrolls virtual rows with a short smooth tail while avoiding long-distance smooth flicker. */
  private scrollVirtualViewportToIndex(index: number): void {
    const viewport = this.viewport;
    if (!viewport) {
      return;
    }

    const targetIndex = Math.max(index, 0);
    const itemSize = this.dropdownConfig().itemSize;
    const currentOffset = viewport.measureScrollOffset('top');
    const currentIndex = Math.floor(currentOffset / itemSize);
    const distance = Math.abs(targetIndex - currentIndex);

    const longJumpThresholdRows = 60;
    const smoothTailRows = 6;

    // Jump close to the target first, then smooth only the last few rows.
    if (distance > longJumpThresholdRows) {
      const nearTargetIndex = targetIndex > currentIndex
        ? Math.max(targetIndex - smoothTailRows, 0)
        : targetIndex + smoothTailRows;

      viewport.scrollToIndex(nearTargetIndex, 'auto');
      requestAnimationFrame(() => viewport.scrollToIndex(targetIndex, 'smooth'));
      return;
    }

    viewport.scrollToIndex(targetIndex, 'smooth');
  }

  /** Finds the index of the selected value inside the filtered item list. */
  private findFilteredIndex(targetValue: unknown): number {
    if (Array.isArray(targetValue)) {
      const firstValue = targetValue[0];
      return this.filteredItems().findIndex((item) => this.rawValueOf(item) === firstValue);
    }

    return this.filteredItems().findIndex((item) => this.rawValueOf(item) === targetValue);
  }

  /** Finds the row index for a selected value when grouped rows are rendered. */
  private findRenderedRowIndex(targetValue: unknown): number {
    return this.renderedRows().findIndex((row) =>
      this.isGroupRow(row) ? false : row.item ? this.rawValueOf(row.item) === targetValue : false
    );
  }

  /** Returns normalized item data for rows that are selectable options. */
  private getRowItem(row: any): Record<string, any> | null {
    if (!row || typeof row !== 'object') {
      return null;
    }

    if (this.isSelectAllRow(row) || this.isGroupRow(row) || this.isSectionHeaderRow(row) || this.isSectionDividerRow(row)) {
      return null;
    }

    if (this.isPinnedRow(row)) {
      return row.item;
    }

    if (row.type === 'item' && row.item) {
      return row.item;
    }

    return row as Record<string, any>;
  }

  /** Returns the first selected option key that is still present in the filtered navigable rows. */
  private selectedNavigableOptionKey(options: NavigableOption[]): string | null {
    const selectedKeys = this.selectedValueSet();
    const selectedOption = options.find((option) => option.kind === 'item' && !!option.item && selectedKeys.has(this.selectionKey(this.rawValueOf(option.item))));
    return selectedOption?.key ?? null;
  }

  /** Extracts section info from an option key prefix (`selected-*` or `main-*`). */
  private sectionFromOptionKey(optionKey: string | null): 'selected' | 'main' | null {
    if (!optionKey) {
      return null;
    }

    if (optionKey.startsWith('selected-')) {
      return 'selected';
    }

    if (optionKey.startsWith('main-')) {
      return 'main';
    }

    return null;
  }

  /** Returns the first navigable key in a preferred section, if any. */
  private firstOptionKeyInSection(
    options: NavigableOption[],
    section?: 'selected' | 'main' | null
  ): string | null {
    if (!section) {
      return null;
    }

    return options.find((option) => option.section === section)?.key ?? null;
  }

  /** Returns the next available option key near the last active row index. */
  private optionKeyNearRowIndex(
    options: NavigableOption[],
    rowIndex: number | null,
    section?: 'selected' | 'main' | null
  ): string | null {
    if (rowIndex === null) {
      return null;
    }

    const scopedOptions = section ? options.filter((option) => option.section === section) : options;
    if (!scopedOptions.length) {
      return null;
    }

    const nextInOrder = scopedOptions.find((option) => option.rowIndex >= rowIndex);
    if (nextInOrder) {
      return nextInOrder.key;
    }

    return scopedOptions[scopedOptions.length - 1].key;
  }

  /** Re-aligns the active option after visible rows change (search/selection/view/sort). */
  private syncActiveOptionWithRows(options: NavigableOption[]): void {
    if (!this.dropdownConfig().multiSelect) {
      this.lastInteractedSection.set(null);
      return;
    }

    if (!options.length) {
      this.activeOptionKey.set(null);
      this.lastActiveRowIndex.set(null);
      this.lastInteractedSection.set(null);
      return;
    }

    const activeKey = this.activeOptionKey();
    if (!activeKey) {
      return;
    }

    const activeIndex = this.navigationIndexMap().get(activeKey);
    if (activeIndex !== undefined) {
      this.lastActiveRowIndex.set(options[activeIndex].rowIndex);
      return;
    }

    const preferredSection = this.sectionFromOptionKey(activeKey) ?? this.lastInteractedSection();
    const fallbackKey = this.optionKeyNearRowIndex(options, this.lastActiveRowIndex(), preferredSection)
      ?? this.firstOptionKeyInSection(options, preferredSection)
      ?? options[0].key;

    this.activeOptionKey.set(fallbackKey);

    const fallbackIndex = this.navigationIndexMap().get(fallbackKey);
    this.lastActiveRowIndex.set(fallbackIndex !== undefined ? options[fallbackIndex].rowIndex : null);
    this.lastInteractedSection.set(this.sectionFromOptionKey(fallbackKey));
  }

  /** Resolves a raw selection back to the matching source item(s). */
  private resolveSelection(selected: any): Record<string, any> | Record<string, any>[] | null {
    if (Array.isArray(selected)) {
      const seen = new Set<string>();
      return [...this.items(), ...this.selectedItemsFromCache()].filter((item) => {
        const itemValue = this.dropdownConfig().valueKey ? item[this.dropdownConfig().valueKey] : item;
        const match = selected.some((value) => value === itemValue);
        const key = this.selectionKey(itemValue);

        if (!match || seen.has(key)) {
          return false;
        }

        seen.add(key);
        return true;
      });
    }

    const vKey = this.dropdownConfig().valueKey;
    const items = this.dropdownConfig().remoteFilter ? [...this.items(), ...this.selectedItemsFromCache()] : this.items();

    return items.find((item) => (vKey ? item[vKey] : item) === selected) ?? null;
  }

  /** Returns whether the select-all option should be displayed. */
  private shouldShowSelectAllOption(): boolean {
    return this.dropdownConfig().multiSelect && this.isSelectAllOptionEnabled();
  }

  /** Supports both the old and new config flag names for select-all behavior. */
  private isSelectAllOptionEnabled(): boolean {
    return !!this.dropdownConfig().selectAllOption;
  }

  /** Switches between list and grouped rendering modes. */
  changeView() {
    this.viewMode.set(this.viewMode() === 'list' ? 'group' : 'list');
    this.activeOptionKey.set(null);
    if (this.isOpen() && this.selectedItemIds().length > 0) {
      this.autoScrollToSelectedValue();
    }
  }

  /** Updates the active sort mode used by both list and grouped views. */
  setSortMode(mode: 'recent' | 'asc' | 'desc'): void {
    this.sortMode.set(mode);
    this.autoScrollToSelectedValue();
  }

  /** Tracks whether the sort menu is open so the trigger can reflect active state. */
  onSortMenuOpened(): void {
    this.isSortMenuOpen.set(true);
  }

  /** Tracks whether the sort menu is closed so the trigger can return to idle state. */
  onSortMenuClosed(): void {
    this.isSortMenuOpen.set(false);
  }

  /** Returns the tooltip text for the sort button based on the active mode. */
  sortTooltipLabel(): string {
    switch (this.sortMode()) {
      case 'recent':
        return this.dropdownConfig().orgSelection ? 'Sort by: Recently Created Organization' : 'Sort by: Recently Created Sites';
      case 'asc':
        return 'Sort by: Ascending (A-Z)';
      case 'desc':
        return 'Sort by: Descending (Z-A)';
      default:
        return 'Sort by';
    }
  }

  /** Resets the search term, sort mode, and view mode to their default states. */
  resetFiltersAndSort(): void {
    this.searchTerm.set('');
    this.sortMode.set('recent');
    this.viewMode.set('list');
  }

  // Closes the dropdown when the iframe loses focus (window blur event).
  @HostListener('window:blur')
  onWindowBlur() {
    this.onBackdropClick();
  }
}
